import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LAYERS } from './layers';
import useModal from './useModal';

/*
 * The app's modal dialog (2026-10-18). Of 35 dialogs, 14 closed on Escape,
 * 7 kept Tab inside and 14 had no accessible name; each re-declared its own
 * overlay. A dialog built on this one gets all of it the same way:
 *
 *   - a name: `title` is required and labels the dialog (aria-labelledby);
 *   - a way out: Escape and the x (labelled "Close") call onClose;
 *   - focus goes in when it opens -- to `initialFocusRef`, else the first
 *     thing in it that takes focus (a confirmation's Cancel, a form's first
 *     field) -- stays in while Tab cycles, and goes back to whatever had it
 *     when it closes;
 *   - a click on the dimmed page closes it, unless `dirty` says something
 *     has been typed that the click would throw away;
 *   - `busy` (saving, deleting) holds it open by every route, Escape
 *     included, so nothing half-done is abandoned.
 *
 * Escape is taken from the document in the capture phase and only by the
 * innermost open Dialog, so a confirmation over a drawer closes the
 * confirmation, not the drawer beneath it. Report an issue's dialog listens
 * on the window, earlier still, and keeps its own Escape. The focus and
 * Escape rules are ./useModal.js, shared with the Sheet variant (./Sheet.js).
 *
 *   <Dialog title={heading} onClose={close} busy={saving} dirty={changed}>
 *     ...fields...
 *     <ActionBar>
 *       <Button onClick={close}>{cancelText}</Button>
 *       <Button variant="primary" onClick={save}>{saveText}</Button>
 *     </ActionBar>
 *   </Dialog>
 *
 * Rendered in place, not in a portal, as the dialogs it replaces were: the
 * overlay is position: fixed, and tests find it inside what rendered it.
 *
 * The panel scrolls as a whole when its content is taller than the screen.
 * A dialog that keeps its header and buttons in sight and scrolls one part
 * of itself instead (Home's image source picker) names its own panel class
 * and style (`panelClassName`, `panelStyle`, laid over the defaults).
 */

export default function Dialog({ open = true, ...props }) {
  if (!open) return null;
  return <OpenDialog {...props} />;
}

function OpenDialog({
  title, onClose, busy = false, dirty = false, showClose = true,
  titleAs: Title = 'h2', titleStyle, layer = LAYERS.dialog, width = 560,
  initialFocusRef, describedBy, testId, panelClassName, panelStyle: ownPanelStyle, children,
}) {
  const { t } = useTranslation('common');
  const titleId = useId();
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);
  const pressedInside = useRef(false);
  useModal(panelRef, { onClose, busy, initialFocusRef, skipRef: closeButtonRef });

  const close = () => { if (!busy) onClose?.(); };

  return (
    <div style={{ ...overlayStyle, zIndex: layer }} data-testid={testId ? `${testId}-backdrop` : undefined}
      onMouseDown={(e) => { pressedInside.current = e.target !== e.currentTarget; }}
      onClick={(e) => {
        // A press that began inside (selecting text, dragging a slider) and
        // ended on the backdrop is not a click on the backdrop.
        const inside = pressedInside.current;
        pressedInside.current = false;
        if (e.target !== e.currentTarget || inside) return;
        if (!dirty) close();
      }}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
        aria-describedby={describedBy} tabIndex={-1} data-testid={testId} className={panelClassName}
        style={{ ...panelStyle, width, ...ownPanelStyle }}>
        <div style={headerStyle}>
          <Title id={titleId} style={{ ...titleBaseStyle, ...titleStyle }}>{title}</Title>
          {showClose && (
            <button ref={closeButtonRef} type="button" onClick={close} disabled={busy}
              aria-label={t('close')} title={t('close')} style={closeStyle}
              data-testid={testId ? `${testId}-close` : undefined}>
              <span aria-hidden="true">{'×'}</span>
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const panelStyle = {
  background: 'var(--c-surface)',
  color: 'var(--c-text-body-fg)',
  borderRadius: 12,
  padding: 20,
  maxWidth: '100%',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  outline: 'none',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBlockEnd: 12,
};

const titleBaseStyle = { margin: 0, fontSize: 18, lineHeight: 1.3 };

const closeStyle = {
  background: 'none',
  border: 'none',
  fontSize: 22,
  lineHeight: 1,
  padding: '2px 6px',
  cursor: 'pointer',
  color: 'var(--c-text-muted-fg)',
  marginInlineStart: 'auto',
};
